import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, MailWarning, Truck } from 'lucide-react';
import { api } from '@/api/client';

/**
 * Landing page for the one-click link mailed to existing merchants.
 *
 * Public and login-free by design — the whole point of the backfill campaign is that a merchant
 * confirms their address from their inbox without having to remember a password. The token in the
 * query string is the only credential; the server matches it by hash and nothing here is trusted.
 */
export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [status, setStatus] = useState<'working' | 'ok' | 'error'>('working');
  const [message, setMessage] = useState('');
  const [company, setCompany] = useState('');
  // React 18 StrictMode double-mounts effects in dev, and this one CONSUMES a single-use token —
  // without the latch the second call reports "already used" over a perfectly good verification.
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    if (!token) { setStatus('error'); setMessage('This link is missing its verification token.'); return; }
    api.post('/auth/verify-email', { token })
      .then((r) => {
        setStatus('ok');
        setCompany(r.data?.companyName ?? '');
        setMessage(r.data?.alreadyVerified ? 'This address was already confirmed — nothing more to do.' : 'Thank you. Your email address is confirmed.');
      })
      .catch((e) => {
        setStatus('error');
        setMessage(e?.response?.data?.message ?? 'We could not verify this link. Please request a new one.');
      });
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50 p-6">
      <div className="bg-white rounded-xl border border-slate-200 shadow-xl p-8 max-w-md w-full text-center">
        <div className="flex items-center justify-center gap-2 mb-5 text-slate-400">
          <Truck size={18} /> <span className="text-sm font-medium">Gaint Logistics</span>
        </div>

        {status === 'working' && (
          <>
            <Loader2 size={44} className="mx-auto text-primary animate-spin mb-3" />
            <h1 className="text-lg font-bold text-slate-900">Confirming your email…</h1>
          </>
        )}

        {status === 'ok' && (
          <>
            <CheckCircle2 size={48} className="mx-auto text-emerald-600 mb-3" />
            <h1 className="text-xl font-bold text-slate-900 mb-2">Email verified</h1>
            {company && <p className="text-sm text-slate-500 mb-1">{company}</p>}
            <p className="text-sm text-slate-600">{message}</p>
          </>
        )}

        {status === 'error' && (
          <>
            <MailWarning size={48} className="mx-auto text-amber-500 mb-3" />
            <h1 className="text-xl font-bold text-slate-900 mb-2">We could not verify this link</h1>
            <p className="text-sm text-slate-600">{message}</p>
            <p className="text-xs text-slate-400 mt-3">
              Need a new link? Email <a className="text-primary" href="mailto:it1@gaintlogistic.com">it1@gaintlogistic.com</a> and we will send one.
            </p>
          </>
        )}

        <Link to="/login" className="mt-6 inline-block px-5 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90">
          Go to Login
        </Link>
      </div>
    </div>
  );
}
